import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { listTalleres, createTaller, updateTaller, deleteTaller } from '../../../redux/actions/talleres';
import WorkshopModal from '../WorkshopModal';
import ConfirmationModal from '../../ui/ConfirmationModal';
import Portal from '../../ui/Portal';
import { MdAdd, MdDelete, MdEdit, MdSchool } from 'react-icons/md';
import styles from './WorkshopManagement.module.css';
import list from '../../../styles/shared/ManagementList.module.css';
import actions from '../../../styles/shared/RequestActions.module.css';
import { TALLER_SEPARATOR } from '../../../consts';

const toDateInputValue = (value) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

// "2025-03-12..." → "12-03-2025" (sin pasar por Date, para no correr el día por la zona horaria)
const formatDate = (value) => {
  const [year, month, day] = toDateInputValue(value).split('-');
  return day ? `${day}-${month}-${year}` : '';
};

const WorkshopManagement = () => {
  const dispatch = useDispatch();
  const { talleres, loading, error } = useSelector((state) => state.Taller);

  const [formValues, setFormValues] = useState({
    id: '',
    nombre: '',
    descripcion: '',
    fechaInicio: '',
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formError, setFormError] = useState('');
  // Taller a eliminar mientras se muestra la confirmación (null = cerrada)
  const [tallerToDelete, setTallerToDelete] = useState(null);

  useEffect(() => {
    dispatch(listTalleres());
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
    if (name === 'nombre' && formError) {
      setFormError('');
    }
  };

  const validateTallerName = (nombre) => {
    if (!nombre || !String(nombre).trim()) {
      return 'El nombre del taller es obligatorio.';
    }
    if (String(nombre).includes(TALLER_SEPARATOR)) {
      return `El nombre del taller no puede contener el carácter "${TALLER_SEPARATOR}" porque se usa como separador de talleres.`;
    }
    return null;
  };

  const handleCreateTaller = async (e) => {
    e.preventDefault();
    const { nombre, descripcion, fechaInicio } = formValues;
    const err = validateTallerName(nombre);
    if (err) {
      setFormError(err);
      return;
    }
    if (!fechaInicio) {
      setFormError('La fecha de inicio es obligatoria.');
      return;
    }
    const result = await dispatch(createTaller(nombre.trim(), descripcion, fechaInicio));
    if (result && result.success) {
      setFormError('');
      dispatch(listTalleres()); // Reload the taller list
      closeModal();
    } else {
      setFormError((result && result.detail) || 'Error al crear el taller.');
    }
  };

  const handleUpdateTaller = async (e) => {
    e.preventDefault();
    const { id, nombre, descripcion, fechaInicio } = formValues;
    const err = validateTallerName(nombre);
    if (err) {
      setFormError(err);
      return;
    }
    if (!fechaInicio) {
      setFormError('La fecha de inicio es obligatoria.');
      return;
    }
    const result = await dispatch(updateTaller(id, nombre.trim(), descripcion, fechaInicio));
    if (result && result.success) {
      setFormError('');
      dispatch(listTalleres()); // Reload the taller list
      closeModal();
    } else {
      setFormError((result && result.detail) || 'Error al actualizar el taller.');
    }
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setFormError('');
    setFormValues({
      id: '',
      nombre: '',
      descripcion: '',
      fechaInicio: '',
    });
    setIsModalOpen(true);
  };

  const openEditModal = (taller) => {
    setIsEditMode(true);
    setFormError('');
    setFormValues({
      id: taller.id,
      nombre: taller.nombre,
      descripcion: taller.descripcion,
      fechaInicio: toDateInputValue(taller.fecha_inicio),
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setFormError('');
    setFormValues({
      id: '',
      nombre: '',
      descripcion: '',
      fechaInicio: '',
    });
  };

  const handleDeleteTaller = (id) => {
    dispatch(deleteTaller(id)).then(({ success, detail }) => {
      if (success) {
        dispatch(listTalleres());
      } else {
        window.alert(detail || 'Error al eliminar el taller.');
      }
    });
  };

  const sortedTalleres = talleres ? [...talleres].sort((a, b) => a.nombre.localeCompare(b.nombre)) : [];

  return (
    <div className={list.container}>
      <section className={list.innerContainer} aria-labelledby="workshops-title">
        <header className={list.header}>
          <div>
            <h2 id="workshops-title" className={list.title}>Gestionar talleres</h2>
            <p className={list.count}>
              {sortedTalleres.length} {sortedTalleres.length === 1 ? 'taller' : 'talleres'}
            </p>
          </div>
          <button type="button" className={list.createButton} onClick={openCreateModal}>
            <MdAdd aria-hidden="true" />
            Crear taller
          </button>
        </header>

        {loading && <p className={list.loading}>Cargando...</p>}
        {error && <p className={list.error}>Error: {error}</p>}

        <WorkshopModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={isEditMode ? handleUpdateTaller : handleCreateTaller}
          formValues={formValues}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          formError={formError}
        />

        <Portal>
          <ConfirmationModal
            isOpen={Boolean(tallerToDelete)}
            onClose={() => setTallerToDelete(null)}
            onConfirm={() => {
              handleDeleteTaller(tallerToDelete.id);
              setTallerToDelete(null);
            }}
            message={tallerToDelete ? `¿Desea eliminar "${tallerToDelete.nombre}"?` : ''}
          />
        </Portal>

        {!loading && sortedTalleres.length === 0 ? (
          <p className={list.empty}>No hay talleres.</p>
        ) : (
          <ul className={list.list} aria-label="Lista de talleres">
            {sortedTalleres.map((taller) => (
              <li key={taller.id} className={list.item}>
                <span className={list.avatar} aria-hidden="true"><MdSchool /></span>
                <div className={list.info}>
                  <p className={list.name}>{taller.nombre}</p>
                  {taller.descripcion && <p className={list.detail}>{taller.descripcion}</p>}
                </div>
                <div className={list.tags}>
                  {taller.fecha_inicio && (
                    <span className={styles.date}>Inicio {formatDate(taller.fecha_inicio)}</span>
                  )}
                </div>
                <div className={`${actions.group} ${list.actions}`}>
                  <button
                    type="button"
                    className={actions.iconButton}
                    onClick={() => openEditModal(taller)}
                    aria-label={`Editar el taller ${taller.nombre}`}
                    title="Editar"
                  >
                    <MdEdit aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className={`${actions.iconButton} ${actions.danger}`}
                    onClick={() => setTallerToDelete(taller)}
                    aria-label={`Eliminar el taller ${taller.nombre}`}
                    title="Eliminar"
                  >
                    <MdDelete aria-hidden="true" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default WorkshopManagement;
